import { Injectable } from '@angular/core';
import { decode } from '@msgpack/msgpack';
import { Observable, Subscriber } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class StreamService {
  private eventSource: EventSource | null = null;

  private createEventSource(url: string, options: EventSourceInit): EventSource {
    return new EventSource(url, options);
  }

  /** Decode SSE payload off the Angular zone — callers re-enter when updating UI. */
  private decodeEventPayload(event: MessageEvent): unknown {
    if (typeof event.data === 'string') {
      try {
        return JSON.parse(event.data);
      } catch {
        const base64String = atob(event.data);
        const bufferData = new Uint8Array(base64String.length);
        for (let i = 0; i < base64String.length; i++) {
          bufferData[i] = base64String.charCodeAt(i);
        }
        return decode(bufferData);
      }
    }
    if (event.data instanceof ArrayBuffer) {
      return decode(new Uint8Array(event.data));
    }
    throw new Error('Unexpected SSE data format');
  }

  connectToServerSentEvents(
    url: string,
    options: EventSourceInit = {},
    eventNames: string[] = []
  ): Observable<{ type: string; data: unknown }> {
    this.eventSource = this.createEventSource(url, options);

    return new Observable((subscriber: Subscriber<{ type: string; data: unknown }>) => {
      this.eventSource!.onerror = (error) => {
        subscriber.error(error);
      };

      eventNames.forEach((eventName) => {
        this.eventSource!.addEventListener(eventName, (event: MessageEvent) => {
          try {
            const data = this.decodeEventPayload(event);
            subscriber.next({ type: event.type, data });
          } catch (err) {
            console.error('Error decoding SSE payload:', err);
            subscriber.error(err);
          }
        });
      });

      return () => {
        this.close();
      };
    });
  }

  close(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }
}
